// services/visibilityService.js
import apiService from './api';
import { ENDPOINTS } from './endpoints';

const PAGE_ID = 'pl_scenario_playground';
const PREFERENCE_KEY = 'hidden_line_items';

class VisibilityService {
  /**
   * Get hidden line items for current user
   * @returns {Promise<string[]>} Array of account IDs
   */
  async getHiddenItems() {
    try {
      const response = await apiService.get(
        ENDPOINTS.USER_PREFERENCE(PAGE_ID, PREFERENCE_KEY)
      );
      
      return response?.preference_value?.hiddenItems || [];
    } catch (error) {
      // If preference doesn't exist yet, return empty array
      if (error.status === 404) {
        return [];
      }
      throw error;
    }
  }

  /**
   * Save hidden line items for current user
   * @param {string[]} accountIds - Array of account IDs to hide
   * @returns {Promise<Object>}
   */
  async saveHiddenItems(accountIds) {
    try {
      const preferenceValue = {
        hiddenItems: accountIds,
        updated_at: new Date().toISOString()
      };

      const response = await apiService.post(
        ENDPOINTS.USER_PREFERENCE(PAGE_ID, PREFERENCE_KEY),
        preferenceValue
      );

      return response;
    } catch (error) {
      console.error('Error saving hidden items:', error);
      throw error;
    }
  }

  /**
   * Add items to hidden list
   * @param {string[]} accountIds - Account IDs to add
   * @returns {Promise<string[]>} Updated hidden items list
   */
  async addHiddenItems(accountIds) {
    try {
      const currentHidden = await this.getHiddenItems();
      const updatedHidden = [...new Set([...currentHidden, ...accountIds])];
      await this.saveHiddenItems(updatedHidden);
      return updatedHidden;
    } catch (error) {
      console.error('Error adding hidden items:', error);
      throw error;
    }
  }

  /**
   * Remove items from hidden list
   * @param {string[]} accountIds - Account IDs to remove
   * @returns {Promise<string[]>} Updated hidden items list
   */
  async removeHiddenItems(accountIds) {
    try {
      const currentHidden = await this.getHiddenItems();
      const updatedHidden = currentHidden.filter(id => !accountIds.includes(id));
      await this.saveHiddenItems(updatedHidden);
      return updatedHidden;
    } catch (error) {
      console.error('Error removing hidden items:', error);
      throw error;
    }
  }

  /**
   * Clear all hidden items
   * @returns {Promise<void>}
   */
  async clearAllHiddenItems() {
    try {
      await apiService.delete(
        ENDPOINTS.USER_PREFERENCE(PAGE_ID, PREFERENCE_KEY)
      );
    } catch (error) {
      console.error('Error clearing hidden items:', error);
      throw error;
    }
  }
}

const visibilityService = new VisibilityService();
export default visibilityService;